/**
 * Store link correction
 *
 * Content (metaobjects, rich text, templates) is shared between the BODi stores, so it can
 * contain absolute links to a sibling store, e.g. https://shop-ca.stage.bodi.com/products/x
 * while the visitor is on https://www.stage.bodi.com. Those links are pointed back at the
 * store the visitor is on, keeping path, query and hash, and adding the visitor's locale
 * prefix (e.g. /fr) when the path does not already have it.
 *
 * Only sibling store hosts of the same environment are touched (stage -> stage, qa -> qa,
 * prod -> prod). Customer account hosts follow the store they belong to, so on shop-ca.stage
 * account.shop-us.stage.bodi.com/profile becomes account.shop-ca.stage.bodi.com/profile (same
 * mapping as snippets/account-profile-url.liquid; no locale prefix on account hosts).
 * Everything else (faq., other environments, third parties) is left alone.
 * Opt a link out with data-keep-store-link; the locale switcher and redirect modal are skipped
 * because they exist to link to the other stores.
 */
(function () {
  // www / shop / shop-us / shop-ca / shop-gb [.stage|.qa] .bodi.com
  const STORE_HOST = /^(?:www|shop(?:-[a-z]{2})?)\.((?:stage|qa)\.)?bodi\.com$/i;
  // account.shop-us.stage.bodi.com etc.
  const ACCOUNT_HOST = /^account\.shop(?:-[a-z]{2})?\.((?:stage|qa)\.)?bodi\.com$/i;
  // www.bodi.com also serves marketing pages as /us/en/..., which don't exist on the stores
  const MARKETING_PATH = /^\/[a-z]{2}\/[a-z]{2}(\/|$)/i;
  const SKIP = '.locale-switcher, .redirect-modal, [data-keep-store-link]';

  const currentHost = window.location.hostname.toLowerCase();
  const currentMatch = currentHost.match(STORE_HOST);
  if (!currentMatch) return; // myshopify.com, theme editor preview, localhost, etc.

  const currentEnv = (currentMatch[1] || '').toLowerCase();
  const currentAccountHost = currentHost.startsWith('shop') ? 'account.' + currentHost : null;
  const localePrefix = (window.Shopify?.routes?.root || '/').replace(/\/$/, ''); // '' or '/fr'

  // rich text editors copy the URL into title="", so keep the tooltip in step with the href
  function setHref(link, oldHref, newHref) {
    link.setAttribute('href', newHref);
    if (link.getAttribute('title') === oldHref) link.setAttribute('title', newHref);
  }

  function fixLink(link) {
    if (link.dataset.storeLinkChecked) return;
    link.dataset.storeLinkChecked = 'true';

    const raw = link.getAttribute('href');
    if (!raw || !/^https?:\/\//i.test(raw)) return;

    let url;
    try {
      url = new URL(raw);
    } catch (e) {
      return;
    }

    const host = url.hostname.toLowerCase();
    if (host === currentHost) return;

    if (link.closest(SKIP)) return;

    const accountMatch = host.match(ACCOUNT_HOST);
    if (accountMatch) {
      if (!currentAccountHost || (accountMatch[1] || '').toLowerCase() !== currentEnv) return;
      url.hostname = currentAccountHost;
      setHref(link, raw, url.toString());
      return;
    }

    const match = host.match(STORE_HOST);
    if (!match || (match[1] || '').toLowerCase() !== currentEnv) return;
    if (MARKETING_PATH.test(url.pathname)) return;

    let path = url.pathname;
    if (localePrefix && path !== localePrefix && !path.startsWith(localePrefix + '/')) {
      path = localePrefix + path;
    }

    setHref(link, raw, window.location.origin + path + url.search + url.hash);
  }

  function scan(root) {
    if (root.nodeType !== 1) return;
    if (root.matches('a[href]')) fixLink(root);
    root.querySelectorAll('a[href]').forEach(fixLink);
  }

  function init() {
    scan(document.documentElement);

    // links added after load: cart drawer, search results, section re-renders, etc.
    let pending = [];
    let queued = false;

    new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((node) => pending.push(node)));
      if (queued || !pending.length) return;
      queued = true;

      requestAnimationFrame(() => {
        const nodes = pending;
        pending = [];
        queued = false;
        nodes.forEach(scan);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
